import { useEffect, useRef, useState, type RefObject } from 'react';
import { useLocation } from 'react-router';
import { BiMenu } from 'react-icons/bi';
import { LogoutIconMobile, SettingsIconMobile } from '../../assets/icons/icons';
import Logo from '../ui/Logo';
import mainNavItems from '../../constants/sideNavConstants';
import NavItem, { NavItemProps } from './NavItem';
import { useAuth } from '../../stores/AuthContext';

const SideNavMobile = ({
  navigationTargetRef,
}: {
  navigationTargetRef?: RefObject<HTMLButtonElement | null>;
}) => {
  const { logout } = useAuth();
  const location = useLocation();
  const [toggleMenu, setToggleMenu] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  const closeMenu = () => dialogRef.current?.close();

  useEffect(() => {
    dialogRef.current?.close();
  }, [location.pathname]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => {
      if (desktop.matches) dialogRef.current?.close();
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  const openSettings = () => {
    const dlg = document.getElementById(
      'settings-modal'
    ) as HTMLDialogElement | null;
    closeMenu();
    dlg?.showModal();
  };

  const bottomNavItems = [
    {
      label: 'Settings',
      icon: <SettingsIconMobile />,
      activeIcon: <SettingsIconMobile />,
      to: '#',
      onClick: openSettings,
    },
    {
      label: 'Sign Out',
      icon: <LogoutIconMobile />,
      activeIcon: <LogoutIconMobile />,
      to: '/',
      onClick: () => {
        logout();
        closeMenu();
      },
    },
  ];

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          ref={navigationTargetRef}
          type="button"
          data-reguide-target="NAVIGATION_MOBILE"
          aria-label="Open navigation"
          aria-haspopup="dialog"
          aria-controls="dashboard-mobile-navigation"
          aria-expanded={toggleMenu}
          onClick={() => {
            dialogRef.current?.showModal();
            setToggleMenu(true);
          }}
          className="btn btn-ghost btn-square text-2xl"
        >
          <BiMenu />
        </button>
        <Logo color="orange" size="mini" className="h-8 w-auto" />
      </div>

      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Escape dismisses the modal navigation. */}
      <dialog
        ref={dialogRef}
        id="dashboard-mobile-navigation"
        aria-label="Dashboard navigation"
        className="modal modal-start"
        onClose={() => setToggleMenu(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            closeMenu();
          }
        }}
      >
        <div className="modal-box flex h-dvh max-h-dvh w-72 max-w-[calc(100vw-2rem)] flex-col rounded-none bg-base-100 p-0">
          <div className="flex justify-between items-center p-4">
            <Logo color="orange" />
            <button
              type="button"
              aria-label="Close navigation"
              className="btn btn-sm btn-circle"
              onClick={closeMenu}
            >
              ✕
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            <nav aria-label="Mobile dashboard">
              <ul className="menu gap-1 p-0 w-full">
                {mainNavItems.map((item) => (
                  <NavItem
                    key={item.to}
                    label={item.label}
                    icon={item.icon}
                    to={item.to}
                    isActive={location.pathname.includes(item.to)}
                    isCollapsed={false}
                    onClick={closeMenu}
                    subMenu={item.subMenu as NavItemProps[]}
                  />
                ))}
                {bottomNavItems.map((item) => (
                  <NavItem
                    key={item.to}
                    label={item.label}
                    icon={item.icon}
                    to={item.to}
                    isActive={false}
                    isCollapsed={false}
                    onClick={item.onClick}
                  />
                ))}
              </ul>
            </nav>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button type="submit" aria-label="Close navigation overlay">
            Close
          </button>
        </form>
      </dialog>
    </>
  );
};

export default SideNavMobile;
